import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Pin, PinOff, MessageSquareQuote, TrendingUp, Trophy } from "lucide-react";

export default function AdminSlogans() {
  const [items, setItems] = useState([]);
  const [newText, setNewText] = useState("");
  const [autoPinning, setAutoPinning] = useState(false);

  const load = () => api.get("/slogans/all").then(r => setItems(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newText.trim()) return;
    try {
      await api.post("/slogans", { text: newText.trim(), active: true, pinned: false, impressions: 0, clicks: 0 });
      setNewText(""); load(); toast.success("Slogan added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const update = async (s, patch) => {
    try {
      await api.put(`/slogans/${s.id}`, { ...s, ...patch });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete slogan?")) return;
    await api.delete(`/slogans/${id}`); load(); toast.success("Deleted");
  };

  const sorted = [...items].sort((a,b) => b.clicks - a.clicks);
  const topId = sorted[0]?.id;

  return (
    <div>
      <h1 className="text-3xl font-display font-black tracking-tight flex items-center gap-2"><MessageSquareQuote className="w-6 h-6 text-[#ea580c]" /> Slogans & Analytics</h1>
      <p className="text-sm text-slate-500 mt-2">Slogans rotate under "More than a ride —" on the homepage. Track which one earns the most Book-a-Ride clicks.</p>

      {/* A/B Winner declaration */}
      <div className="mt-6 p-4 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
        <div>
          <div className="font-display font-bold flex items-center gap-2 text-[#1e1b4b]"><Trophy className="w-5 h-5 text-amber-600" /> A/B Winner Auto-Pin</div>
          <div className="text-xs text-slate-600 mt-0.5">Runs weekly via cron. Manually declare a winner now — pins the highest-CTR slogan (min 10 impressions).</div>
        </div>
        <Button onClick={async ()=>{
          setAutoPinning(true);
          try {
            const r = await api.post("/slogans/auto-pin-winner");
            if (r.data.ok) toast.success(`🏆 Winner pinned: "${r.data.winner_text}" · CTR ${r.data.ctr}%`);
            else toast.info(r.data.reason || "Not enough data yet");
            load();
          } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
          setAutoPinning(false);
        }} disabled={autoPinning} className="bg-[#1e1b4b] hover:bg-[#0f1c56]" data-testid="declare-winner-btn">
          {autoPinning ? "Declaring…" : "Declare Winner Now"}
        </Button>
      </div>

      {/* Add new */}
      <div className="mt-6 p-4 rounded-2xl bg-white border border-slate-200 flex flex-col sm:flex-row gap-2">
        <Input value={newText} onChange={e=>setNewText(e.target.value)} placeholder="e.g. every ride, on time." data-testid="new-slogan-input" />
        <Button onClick={add} className="bg-[#ea580c] hover:bg-orange-700" data-testid="add-slogan-btn"><Plus className="w-4 h-4 mr-1" /> Add Slogan</Button>
      </div>

      {/* List */}
      <div className="mt-6 space-y-3">
        {items.map(s => {
          const imp = Math.max(1, s.impressions);
          const ctr = ((s.clicks / imp) * 100).toFixed(1);
          const isTop = s.id === topId && s.clicks > 0;
          return (
            <div key={s.id} className={`p-4 rounded-xl bg-white border ${isTop?"border-amber-400 shadow-md":"border-slate-200"} flex flex-col md:flex-row md:items-center gap-3`} data-testid={`slogan-row-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-display font-bold text-lg text-[#1e1b4b] truncate">{s.text}</div>
                  {s.pinned && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1e1b4b] text-white uppercase tracking-widest">Pinned</span>}
                  {isTop && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400 text-[#1e1b4b] uppercase tracking-widest font-bold flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Top</span>}
                </div>
                <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-3">
                  <span>Impressions: <b>{s.impressions}</b></span>
                  <span>Clicks: <b>{s.clicks}</b></span>
                  <span>CTR: <b>{ctr}%</b></span>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <label className="flex items-center gap-2 text-xs">Active <Switch checked={s.active} onCheckedChange={v=>update(s,{active:v})} data-testid={`toggle-active-${s.id}`} /></label>
                <Button size="sm" variant="outline" onClick={()=>update(s,{pinned:!s.pinned})} data-testid={`pin-${s.id}`}>
                  {s.pinned ? <><PinOff className="w-3 h-3 mr-1" /> Unpin</> : <><Pin className="w-3 h-3 mr-1" /> Pin</>}
                </Button>
                <Button size="sm" variant="outline" onClick={()=>remove(s.id)} data-testid={`delete-slogan-${s.id}`}><Trash2 className="w-3 h-3 mr-1 text-red-500" /> Delete</Button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <div className="p-6 rounded-xl bg-white border border-slate-200 text-sm text-slate-500">No slogans yet. Add your first one above.</div>}
      </div>
    </div>
  );
}
